import {
	EventFilterCondition,
	GPEventResponse,
	GPGamePlayerResponse,
	EventFilterValue,
	VideoSidebar,
	NavOptions,
} from "../../types";
import eventFilterClass from "../Video/Filter/eventFilter";
import { createStatTypeConditions } from "../Video/Filter/conditionFactory";
import { PlayerStatType } from "../PlayerStats/definition";
import { TeamStatType } from "../TeamStats/definition";
import {
	InsightCategoryMap,
	InsightCategoryEnum,
	EventOffensiveEnum,
	EventDefensiveEnum,
	EventDistributionEnum,
	EventGoalkeeperEnum,
	InsightEventGroupEnum,
	FIELD_MAP_SIZE,
	ActionType,
	WidgetItem,
	TeamSideEnum,
	EventType,
} from "./constants";
import { calculatePlayerStats } from "../PlayerStats/functions";
import { _ } from "i18n";
import { EventListTeamInfo } from "./contexts/DataInsightContext";
import { createStatTypeFilterValues } from "../Video/Filter/filterFactory";
import { FilterCategory } from "../../types";
import { SelectOption } from "./ParameterOptions";

/**
 * Check if the event has a follow-up event
 * @param event - The event to check
 * @param nextEvent - The next event to check
 * @returns true if the event has a follow-up event, false otherwise
 */
const hasFollowUpEvent = (
	event: GPEventResponse,
	nextEvent: GPEventResponse,
) => {
	if (event.type === PlayerStatType.Shot) {
		if (
			event?.details?.on_target === ActionType.OnTargetBlocked &&
			nextEvent?.type === ActionType.Block
		) {
			return true;
		}

		if (
			event?.details?.on_target === ActionType.On &&
			nextEvent?.type === ActionType.Save
		) {
			return true;
		}
	}

	if (event.type === PlayerStatType.Pass) {
		if (nextEvent?.type === ActionType.PassReceived && nextEvent?.success) {
			return true;
		}
	}

	return false;
};

const appendNextEventCoordinates = (
	eventFilter: eventFilterClass,
	originalEvents: GPEventResponse[],
	filteredEvents: GPEventResponse[],
): GPEventResponse[] => {
	const withNextEvent = fetchNextEvent(filteredEvents, originalEvents);

	const eventMapById = filteredEvents.reduce((acc, event) => {
		acc[event.id] = event;
		return acc;
	}, {} as Record<number, typeof filteredEvents[number]>);

	for (let i = 0; i < withNextEvent.length - 1; i++) {
		const current = withNextEvent[i];
		const next = withNextEvent[i + 1];

		// Mutate current only if it's from the original filteredEvents list
		if (eventMapById[current.id] && hasFollowUpEvent(current, next)) {
			eventMapById[current.id].details = {
				...eventMapById[current.id].details,
				next_event_coordinates: `{"x":${next?.x || 0},"y":${
					next?.y || 0
				}, "type": "${next?.type || ""}"}`,
			};
		}
	}

	return Object.values(eventMapById);
};

export function groupEventsByCategories(
	events: GPEventResponse[],
	eventFilter: eventFilterClass,
	periods: string[],
): typeof InsightCategoryMap {
	const result: typeof InsightCategoryMap = {
		[InsightCategoryEnum.Offensive]: {
			[EventOffensiveEnum.TakeOn]: [],
			[EventOffensiveEnum.Offside]: [],
			[EventOffensiveEnum.Goal]: [],
			[EventOffensiveEnum.FoulWon]: [],
			[EventOffensiveEnum.Shot]: [],
			[EventOffensiveEnum.TechnicalError]: [],
		},
		[InsightCategoryEnum.Defensive]: {
			[EventDefensiveEnum.OwnGoals]: [],
			[EventDefensiveEnum.FoulCommited]: [],
			[EventDefensiveEnum.Block]: [],
			[EventDefensiveEnum.Interception]: [],
			[EventDefensiveEnum.LoseBallRecovery]: [],
			[EventDefensiveEnum.Tackle]: [],
			[EventDefensiveEnum.Clearance]: [],
			[EventDefensiveEnum.Card]: [],
			[EventDefensiveEnum.TakeOnAgainst]: [],
		},
		[InsightCategoryEnum.Distribution]: {
			[EventDistributionEnum.AerialDuels]: [],
			[EventDistributionEnum.Pass]: [],
			[EventDistributionEnum.PassReceived]: [],
		},
		[InsightCategoryEnum.Goalkeeper]: {
			[EventGoalkeeperEnum.CatchInterception]: [],
			[EventGoalkeeperEnum.Punch]: [],
			[EventGoalkeeperEnum.GoalConceded]: [],
			[EventGoalkeeperEnum.Save]: [],
		},
	};

	// Offensive
	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.TakeOn] =
		filterEvents(eventFilter, PlayerStatType.TakeOn, periods);
	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.Offside] =
		filterEvents(eventFilter, PlayerStatType.Offside, periods);
	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.Goal] = filterEvents(
		eventFilter,
		PlayerStatType.Goal,
		periods,
	);
	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.FoulWon] =
		filterEvents(eventFilter, PlayerStatType.FoulWon, periods);
	const shots = appendNextEventCoordinates(
		eventFilter,
		events,
		filterEvents(eventFilter, PlayerStatType.Shot, periods),
	);

	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.Shot] = shots;
	result[InsightCategoryEnum.Offensive][EventOffensiveEnum.TechnicalError] =
		filterEvents(eventFilter, PlayerStatType.TechnicalError, periods);

	// Defensive
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.Interception] =
		filterEvents(eventFilter, PlayerStatType.Interception, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.Tackle] =
		filterEvents(eventFilter, PlayerStatType.Tackle, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.Clearance] =
		filterEvents(eventFilter, TeamStatType.Clearance, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.TakeOnAgainst] =
		filterEvents(eventFilter, PlayerStatType.TakeOnAgainst, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.LoseBallRecovery] =
		filterEvents(eventFilter, PlayerStatType.LoseBallRecovery, periods);

	const cardYellow = filterEvents(
		eventFilter,
		PlayerStatType.CardYellow,
		periods,
	);
	const cardRed = filterEvents(eventFilter, PlayerStatType.CardRed, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.Card] = [
		...cardYellow,
		...cardRed,
	];
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.OwnGoals] =
		filterEvents(eventFilter, PlayerStatType.GoalOwn, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.FoulCommited] =
		filterEvents(eventFilter, PlayerStatType.FoulCommited, periods);
	result[InsightCategoryEnum.Defensive][EventDefensiveEnum.Block] =
		filterEvents(eventFilter, PlayerStatType.Block, periods);

	// Distribution
	result[InsightCategoryEnum.Distribution][EventDistributionEnum.AerialDuels] =
		filterEvents(eventFilter, PlayerStatType.AerialDuel, periods);
	const passes = appendNextEventCoordinates(
		eventFilter,
		events,
		filterEvents(eventFilter, PlayerStatType.Pass, periods),
	);

	result[InsightCategoryEnum.Distribution][EventDistributionEnum.Pass] = passes;
	result[InsightCategoryEnum.Distribution][EventDistributionEnum.PassReceived] =
		filterEvents(eventFilter, PlayerStatType.PassReceived, periods);

	// Goalkeeper
	result[InsightCategoryEnum.Goalkeeper][
		EventGoalkeeperEnum.CatchInterception
	] = filterEvents(eventFilter, PlayerStatType.CatchInterception, periods);
	result[InsightCategoryEnum.Goalkeeper][EventGoalkeeperEnum.Punch] =
		filterEvents(eventFilter, PlayerStatType.Punch, periods);
	result[InsightCategoryEnum.Goalkeeper][EventGoalkeeperEnum.GoalConceded] =
		filterEvents(eventFilter, PlayerStatType.GoalConceded, periods);
	result[InsightCategoryEnum.Goalkeeper][EventGoalkeeperEnum.Save] =
		filterEvents(eventFilter, PlayerStatType.Save, periods);

	return result;
}

export function filterEvents(
	eventFilter: eventFilterClass,
	statType: PlayerStatType | TeamStatType,
	periods: string[],
	teamId: number | undefined = undefined,
): GPEventResponse[] {
	const conditions = createStatTypeConditions(statType);

	if (conditions.length === 0) {
		return [];
	}

	conditions.push({ type: "period", value: periods });

	if (teamId) {
		conditions.push({ type: "team", value: [teamId.toString()] });
	}

	return eventFilter.filterByConditions(conditions, eventFilter.events);
}

export function transformArrayObjectToDropdownItems(
	array: any[],
	valueMapKey: string,
	labelMapKey: string,
	propertyPrefix: string = "",
) {
	return array.map((item) => ({
		value: item[valueMapKey],
		label: item[propertyPrefix]
			? item[propertyPrefix] + " " + item[labelMapKey]
			: item[labelMapKey],
	}));
}

export function getPlayerDetails(
	event: GPEventResponse,
	players: Record<number, GPGamePlayerResponse>,
): GPGamePlayerResponse | null {
	if (event.gamePlayerId && players[event.gamePlayerId]) {
		return players[event.gamePlayerId];
	}
	return null;
}

export function formatTime(
	clipTime: number | string | null,
	withLabel: boolean = false,
) {
	if (!clipTime) return "00:00";

	clipTime = parseInt(clipTime as string);

	const totalSeconds = Math.floor(clipTime / 1000); // Convert ms → sec
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	const formatted = `${minutes.toString().padStart(2, "0")}:${seconds
		.toString()
		.padStart(2, "0")}`;
	if (withLabel) {
		const split = formatted.split(":");
		return `${split[0]}${_("gamePage.dataInsights.label.min")} ${split[1]}${_(
			"gamePage.dataInsights.label.sec",
		)}`;
	}
	return formatted;
}

export function fetchNextEvent(
	filtered: GPEventResponse[],
	original: GPEventResponse[],
): GPEventResponse[] {
	const result: GPEventResponse[] = [];
	const seen = new Set<number>();

	// Map event.id => index for O(1) lookup
	const idToIndex = new Map<number, number>();
	original.forEach((e, idx) => idToIndex.set(e.id, idx));

	for (const event of filtered) {
		if (!seen.has(event.id)) {
			result.push(event);
			seen.add(event.id);
		}

		const currentIndex = idToIndex.get(event.id);
		if (currentIndex !== undefined && currentIndex + 1 < original.length) {
			const nextEvent = original[currentIndex + 1];
			if (!seen.has(nextEvent.id)) {
				result.push(nextEvent);
				seen.add(nextEvent.id);
			}
		}
	}

	return result;
}

export function generateUniqueKey(prefix?: string) {
	return prefix
		? `${prefix}-${Date.now()}-${Math.random().toString(36)}`
		: `${Date.now()}-${Math.random().toString(36)}`;
}

function getEventCategory(
	statType: PlayerStatType | TeamStatType,
	category: string,
) {
	if (
		statType === PlayerStatType.LoseBallRecovery ||
		statType === PlayerStatType.FoulCommited ||
		statType === PlayerStatType.Card
	) {
		return InsightCategoryEnum.Defensive;
	} else if (
		statType === PlayerStatType.AerialDuel ||
		statType === PlayerStatType.Pass ||
		statType === PlayerStatType.PassReceived
	) {
		return InsightCategoryEnum.Distribution;
	} else if (
		statType === PlayerStatType.Goal ||
		statType === PlayerStatType.FoulWon ||
		statType === PlayerStatType.Offside
	) {
		return InsightCategoryEnum.Offensive;
	}

	return category;
}

export function getEventFilterValuesByCategory(
	category: string,
	statType: PlayerStatType | TeamStatType,
	filters: EventFilterCondition[],
	teamInfo: EventListTeamInfo | null,
	parameterFilters?: Record<string, SelectOption>,
) {
	const filterValues: EventFilterValue[] = [];

	let playerSide = TeamSideEnum.Home;
	filters.forEach((filter) => {
		if (
			filter.type === FilterCategory.Team &&
			teamInfo?.homeTeam.id?.toString() === filter.value[0]
		) {
			filterValues.push({
				key: `${FilterCategory.Team}.home`,
				value: [teamInfo.homeTeam.id.toString()],
			});
			playerSide = TeamSideEnum.Home;
		} else if (
			filter.type === FilterCategory.Team &&
			teamInfo?.awayTeam.id?.toString() === filter.value[0]
		) {
			filterValues.push({
				key: `${FilterCategory.Team}.away`,
				value: [teamInfo.awayTeam.id.toString()],
			});
			playerSide = TeamSideEnum.Away;
		}

		if (filter.type === FilterCategory.Player) {
			filterValues.push({
				key: `${FilterCategory.Player}.${playerSide}`,
				value: filter.value,
			});
		}

		if (filter.type === FilterCategory.Period) {
			filter.value.forEach((value) => {
				filterValues.push({
					key: `${FilterCategory.Period}.${value}`,
					value: [value],
				});
			});
		}
	});

	// Handle lose ball special case
	const eventCategory = getEventCategory(statType, category);
	filterValues.push({
		key: `category.${eventCategory}`,
		value: [eventCategory],
	});

	filterValues.push(...createStatTypeFilterValues(statType));
	if (parameterFilters?.flag?.value) {
		filterValues.push({
			key: `event.${eventCategory}.${statType}.${parameterFilters.flag.flag}`,
			value: [parameterFilters.flag.value],
		});
	} else if (parameterFilters?.success) {
		filterValues.push({
			key: `event.${eventCategory}.${parameterFilters?.success?.flag}.success`,
			value: [parameterFilters.success.value],
		});
	}
	return filterValues;
}

export function getEventDetails(event: GPEventResponse) {
	const prefixKey = "gamePage.playerStats.type.";
	const detailsKeys = Object.keys(event.details || {});
	const details: string[] = [];

	if (event?.success === true) {
		details.push("event.v3.flag.success.true");
	} else if (event?.success === false) {
		details.push("event.v3.flag.success.false");
	}

	if (!detailsKeys.length) {
		return details;
	}

	detailsKeys.forEach((key) => {
		if (key === "inside_pa" && event.type === PlayerStatType.Shot) {
			if (event.details?.inside_pa) {
				details.push(`${prefixKey}shotInsidePa`);
			} else {
				details.push(`${prefixKey}shotOutsidePa`);
			}
		} else if (key === "on_target" && event.type === PlayerStatType.Shot) {
			if (event.details?.on_target === ActionType.OnTargetBlocked) {
				details.push(`${prefixKey}shotBlocked`);
			} else if (event.details?.on_target === ActionType.Off) {
				details.push(`${prefixKey}shotOffTarget`);
			} else {
				details.push(`${prefixKey}shotOnTarget`);
			}
		} else if (key === "goal" && event.type === PlayerStatType.Goal) {
			details.push(`${prefixKey}goal`);
		} else if (key === "own_goal" && event.type === PlayerStatType.GoalOwn) {
			details.push(`${prefixKey}goalOwn`);
		} else if (key === "tackle" && event.type === PlayerStatType.Tackle) {
			details.push(`${prefixKey}tackle`);
		}
	});

	return details;
}

export function slugify(str: string) {
	return str
		.toLowerCase()
		.replace(/[^a-z0-9\s-]/g, "") // remove special characters
		.trim()
		.replace(/\s+/g, "-"); // replace spaces with dash
}

/**
 * Offensive - Should be on top side
 * Defensive - Should be on bottom side
 * @param x - x coordinate
 * @param activeCategoryTab
 * @param customWidget
 * @returns corrected x coordinate
 */
export function correctedXCoordinatePosition(x: number) {
	return 1 - x;
}

export function isDefaultCategory(category: InsightEventGroupEnum) {
	return [
		InsightEventGroupEnum.Offensive,
		InsightEventGroupEnum.Defensive,
		InsightEventGroupEnum.Goalkeeper,
		InsightEventGroupEnum.Distribution,
	].includes(category);
}

export function groupWidgetsByCategory(items: any[]) {
	const grouped: Partial<Record<InsightEventGroupEnum, any[] | null>> = {};

	for (const key in InsightEventGroupEnum) {
		const enumKey =
			InsightEventGroupEnum[key as keyof typeof InsightEventGroupEnum];
		grouped[enumKey] = null;
	}

	for (const item of items) {
		const group = item.group as InsightEventGroupEnum;
		if (!grouped[group]) {
			grouped[group] = [];
		}
		(grouped[group] as any[]).push(item);
	}

	return grouped;
}
